<template>
  <div>
    <order-details
      v-if="getterPopupName === 'OrderDetails'"
      :txnDetails="popupDetails"
      @refreshList="updateFilters"
      :btnOptions="orderOptions"
      :title="'Order Details'"
    />
    <order-create
      v-else-if="getterPopupName === 'OrderCreate'"
      @refreshList="updateFilters"
    />
    <order-filter
      v-else-if="getterPopupName === 'OrderFilter'"
      @updateFilters="updateFilters"
    />
    <order-export
      v-else-if="getterPopupName === 'OrderExport'"
      :exportOptions="paginate"
      :exportRecords="exportRecords"
    />
    <refund-create
      v-else-if="getterPopupName === 'RefundCreate'"
      :details="popupDetails"
    />
    <expand-collapse-list
      v-else-if="getterPopupName === 'OrderPayments'"
      :list="expandList"
      :type="'payments'"
      :extras="{
        show: true,
        title: 'Payer Details',
        key: 'payment_details'
      }"
    />
    <expand-collapse-list
      v-else-if="getterPopupName === 'OrderRefunds'"
      :list="expandList"
    />

    <div class="table-title">
      <div class="subject">
        <p>Orders</p>
        <span v-if="typeof paginate.total === 'number'">
          Total ({{paginate.total}})
        </span>

        <order-webhooks
          v-if="paginate.total && webhooksAllowed"
          :paginate="paginate"
          @resendWebhooks="bulkResendWebhooks"
        />
      </div>

      <div class="filters">
        <div class="options">
          <button
            v-if="access.manageOrders"
            @click="openPopup('OrderCreate')"
          >
            Create Order
            <i class="fa fa-plus" />
          </button>
          <button @click="openPopup('OrderExport')">
            Export
            <i class="fa fa-share" />
          </button>
          <button
            :class="{'filters-applied': filtersApplied}"
            class="dark-btn"
            @click="openPopup('OrderFilter')"
          >
            Filter
            <i class="fa fa-filter" />
          </button>
        </div>
      </div>
    </div>

    <div class="table-head" v-if="isDesktop()">
      <p>Order ID</p>
      <p>Amount</p>
      <p>Expiry Time</p>
      <p>Mobile Num</p>
      <p>Status</p>
      <p class="d-sm"></p>
    </div>

    <div class="table-body">
      <div
        v-for="(data, index) in list" :key="index"
        class="table-row cursor mt-align-self"
        @click="showDetails(data)"
      >
        <p>
          <b v-if="!isDesktop()">Order ID:</b>
          <span>{{data.order_id}}</span>
        </p>
        <p>
          <b v-if="!isDesktop()">Order amt:</b>
          {{amountFormat(data.amount)}}
        </p>
        <p class="mt-grey-text">
          <b v-if="!isDesktop()">Expiry:</b>
          {{data.expiry_time}}
        </p>
        <p>
          <b v-if="!isDesktop()">Mobile num:</b>
          <span>{{data.customer_phone}}</span>
        </p>
        <p class="mt-grey-text">
          {{data.status}}
        </p>
        <p v-if="isDesktop()" class="download d-sm">
          <span>More</span>
          <i class="fa fa-angle-down"></i>

          <output class="more-options">
            <span
              v-for="(item, index) in data.moreOptions"
              :key="index"
              @click="item.action($event, data)"
            >
              {{item.text}}
            </span>
          </output>
        </p>
        <template v-else>
          <p
            v-for="(item, index) in data.moreOptions"
            :key="index"
            @click="item.action($event, data)"
            class="action"
          >
            {{item.text}}
          </p>
        </template>
      </div>
    </div>

    <table-loader v-if="!paginate.data_added || !paginate.stop" />

    <div class="table-empty" v-else-if="paginate.total === 0">
      <p>{{emptyRecords}}</p>
    </div>
  </div>
</template>

<script>
import OrderDetails from '@/components/AccountStatement/StatementDetails'
import OrderFilter from '@/components/PG/Orders/Filters'
import OrderCreate from '@/components/PG/Orders/Create'
import RefundCreate from '@/components/PG/Refunds/Create'
import OrderExport from '@/components/global/ExportConfirmation'
import OrderWebhooks from '@/components/global/BulkResendWebhooks'
import ExpandCollapseList from '@/components/global/ExpandCollapseList'

import ApiServices from '@/services/api/pgOrders'
import listMixins from '@/mixins/listMixins'
import { amountFormat, setDropdownPosOffset } from '@/utils/helpers'

export default {
  name: 'PgOrders',
  components: { OrderDetails, OrderWebhooks, OrderFilter, OrderExport, OrderCreate, RefundCreate, ExpandCollapseList },
  mixins: [listMixins],
  data () {
    return {
      orderOptions: [],
      access: {},
      expandList: [],
      createRefundOption: {
        text: 'Create Refund',
        css: 'outline-btn',
        icon: 'fa-redo',
        action: () => {
          this.openPopup('RefundCreate')
        }
      },
      resendWebhookOption: {
        text: 'Resend Webhook',
        css: 'primary-btn',
        icon: 'fa-paper-plane',
        action: () => {
          this.resendWebhook()
        }
      }
    }
  },
  watch: {
    getterPopupName (newVal) {
      if (newVal === this.keys.popup) {
        this.orderOptions = []

        if (this.popupDetails.webhook_allowed) {
          this.orderOptions.push(this.resendWebhookOption)
        }

        if (this.access.manageRefunds && this.popupDetails.status === 'COMPLETED') {
          this.orderOptions.push(this.createRefundOption)
        }
      }
    }
  },
  created () {
    this.startProcessing()
  },
  methods: {
    amountFormat,
    setDropdownPosOffset,
    populateParams () {
      this.api['bulkResendWebhook'] = ApiServices.bulkResendWebhook
      this.api['export'] = ApiServices.export
      this.api['fetchStatus'] = ApiServices.fetchStatus
      this.api['resendWebhook'] = ApiServices.resendWebhook
      this.api['count'] = ApiServices.count
      this.keys['popup'] = 'OrderDetails'
      this.keys['list'] = 'orders'
      this.paginate['type'] = 'order'
      this.paginate['token_type'] = 'next_page_token'
    },
    fetchRecords (start) {
      return new Promise(resolve => {
        let params = {...this.filterOptions}
        if (start) {
          params['next_page_token'] = this.paginate['next_page_token']
        }

        ApiServices.list(params).then(response => {
          this.SET_STATEMENT_FILTER(this.filterOptions)
          if (response.reason === 'UNPRIVILEGED') {
            return
          }

          let data = response.data
          data.orders = data.orders.map(item => {
            item.moreOptions = []
            item.moreOptions.push({
              action: this.fetchPayments,
              text: 'View Payments'
            })

            if (item.status === 'COMPLETED') {
              item.moreOptions.push({
                action: this.fetchRefunds,
                text: 'View Refunds'
              })
            }
            return item
          })
          this.setAccess(response.privileges)

          if (start) {
            this.list.push(...data.orders)
            if (data.next_page_token) {
              this.paginate['next_page_token'] = data['next_page_token']
            } else {
              this.paginate['stop'] = true
            }
            resolve()
          } else {
            this.list = data.orders
            this.setPaginate(data)
            this.populateMeta(response)

            if (data['next_page_token']) {
              this.getListCount(resolve)
            } else {
              resolve()
            }
          }
        })
      })
    },
    populateMeta ({data}) {
      this.webhooksAllowed = data.webhook_allowed
    },
    setAccess (privileges) {
      this.access['manageOrders'] = privileges.manage_orders
      this.access['manageRefunds'] = privileges.manage_refunds
    },
    fetchRefunds ($event, {id}) {
      $event.stopPropagation()

      ApiServices.fetchRefunds(id).then(response => {
        this.expandList = response.data
        this.openPopup('OrderRefunds')
      })
    },
    fetchPayments ($event, {id}) {
      $event.stopPropagation()

      ApiServices.fetchPayments(id).then(response => {
        this.expandList = response.data
        this.openPopup('OrderPayments')
      })
    }
  }
}
</script>

<style lang="scss" scoped>
  @import '@/assets/scss/table.scss';

  .desktop {
    .table-row {
      .download {
        position: relative;

        .fa-angle-down {
          padding-left: 5px;
        }

        .more-options {
          display: none;
          position: absolute;
          top: 15px;
          left: 0;
          z-index: 10;

          background: $white;
          border-radius: 15px;
          box-shadow: $lite-shadow;

          span {
            padding: 20px;
            white-space: nowrap;

            &:not(:last-child) {
              border-bottom: $divider;
            }

            &:hover {
              background: rgba(101, 109, 113, .05);
            }
          }
        }

        &:hover {
          .more-options {
            display: grid;
          }
        }
      }
    }
  }

  .mobile, .tablet {
    .action {
      color: $primary !important;
    }
  }
</style>
